import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { listJobs } from "../../services/jobsApi";
import {
  DEPARTMENT_LABELS,
  EMPLOYMENT_LABELS,
  EmploymentPill,
  JobPill,
  JobsShell,
} from "./jobsShared";

const TYPE_FILTERS = ["", "full_time", "part_time", "commission_only", "contract"];

export default function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [type, setType] = useState("");
  const [department, setDepartment] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    listJobs()
      .then((res) => {
        if (!cancelled) setJobs(res.jobs || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Could not load positions");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const departments = useMemo(
    () => Array.from(new Set(jobs.map((job) => job.department))).filter(Boolean),
    [jobs]
  );

  const visible = useMemo(
    () =>
      jobs.filter(
        (job) =>
          (!type || job.employment_type === type) &&
          (!department || job.department === department)
      ),
    [jobs, type, department]
  );

  return (
    <JobsShell>
      <Helmet>
        <title>Open positions - QikTruck Careers</title>
        <meta
          name="description"
          content="Open positions at QikTruck. Sales, logistics admin, part time and commission only roles. Apply with a short intro video."
        />
        <link rel="canonical" href="https://careers.qiktruck.co.za/jobs" />
      </Helmet>

      <section className="careers-section jobs-hero">
        <p className="careers-eyebrow">Work with QikTruck</p>
        <h1 className="careers-h1">Open positions</h1>
        <p className="careers-lead">
          Pick a role, sign in with your email, and apply with a short intro video. Tell us who
          you are in your own words.
        </p>
      </section>

      <section className="careers-section careers-section--tight">
        <div className="jobs-filters" role="group" aria-label="Filter positions">
          {TYPE_FILTERS.map((value) => (
            <button
              key={value || "all"}
              type="button"
              className={`jobs-filter${type === value ? " is-active" : ""}`}
              onClick={() => setType(value)}
            >
              {value ? EMPLOYMENT_LABELS[value] : "All roles"}
            </button>
          ))}
          {departments.length > 1 ? (
            <select
              className="jobs-filter-select"
              value={department}
              onChange={(event) => setDepartment(event.target.value)}
              aria-label="Department"
            >
              <option value="">All departments</option>
              {departments.map((value) => (
                <option key={value} value={value}>
                  {DEPARTMENT_LABELS[value] || value}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        {loading ? <p className="jobs-note">Loading positions...</p> : null}
        {error ? <p className="jobs-note jobs-note--error">{error}</p> : null}

        {!loading && !error && visible.length === 0 ? (
          <div className="jobs-empty">
            <h2>No open roles right now</h2>
            <p>
              {jobs.length > 0
                ? "No roles match that filter. Try another one."
                : "We post new roles here as soon as they open. Sign in and finish a training program so you are ready when one opens."}
            </p>
            <Link to="/academy" className="careers-btn careers-btn--secondary">
              Browse training programs
            </Link>
          </div>
        ) : null}

        <div className="jobs-list">
          {visible.map((job) => (
            <Link key={job.id} to={`/jobs/${job.slug}`} className="job-card">
              <div className="job-card__top">
                <h2 className="job-card__title">{job.title}</h2>
                <div className="job-card__pills">
                  <EmploymentPill type={job.employment_type} />
                  <JobPill tone="slate">
                    {DEPARTMENT_LABELS[job.department] || job.department}
                  </JobPill>
                  {job.is_remote ? <JobPill tone="green">Remote</JobPill> : null}
                </div>
              </div>
              {job.summary ? <p className="job-card__summary">{job.summary}</p> : null}
              <div className="job-card__meta">
                {job.pay_summary ? <span>{job.pay_summary}</span> : null}
                {job.location ? <span>{job.location}</span> : null}
                {job.video_required ? <span>Intro video</span> : null}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </JobsShell>
  );
}
